import React, { useState } from 'react';
import { WorkflowComponentProps } from '../../Workflow/Workflow.meta';
import { useOtherAssetsQuery } from './api/useOtherAssetsQuery';
import { Button } from '../../CommonComponents/OPL/Button/Button';
import { WorkflowFooter } from '../../Workflow/components/WorkflowFooter/WorkflowFooter';
import { RealEstate } from './tables/RealEstate/RealEstate';
import { Business } from './tables/Business/Business';
import { Pensions } from './tables/Pensions/Pensions';
import { MiscAssets } from './tables/MiscAssets/MiscAssets';
import { DeferredCompensation } from './tables/DeferredCompensation/DeferredCompensation';
import { UpsertAssetModal } from './components/UpsertAssetModal/UpsertAssetModal';
import { DeleteAssetModal } from './components/DeleteAssetModal/DeleteAssetModal';
import { AssetToUpsert } from './components/UpsertAssetModal/UpsertAssetModal.meta';
import { AssetToDelete } from './components/DeleteAssetModal/DeleteAssetModal.meta';
import { OtherAssetType } from './OtherAssets.meta';
import { ScrollHereOnce } from '../../CommonComponents/ScrollHereOnce';
import { RealEstateForm } from './forms/RealEstate/RealEstateForm';
import { RealEstateDTO } from './tables/RealEstate/RealEstate.meta';
import { BusinessAssetsForm } from './forms/BusinessAssets/BusinessAssetsForm';
import { BusinessAssetDTO } from './tables/Business/Business.meta';
import { PensionForm } from './forms/Pension/PensionForm';
import { PensionDTO } from './tables/Pensions/Pensions.meta';
import { MiscAssetsForm } from './forms/MiscAssets/MiscAssetsForm';
import { MiscAssetDTO } from './tables/MiscAssets/MiscAssets.meta';
import { DeferredCompensationForm } from './forms/DeferredCompensation/DeferredCompensationForm';
import { DeferredCompensationDTO } from './tables/DeferredCompensation/DeferredCompensation.meta';
import { Box } from '../../CommonComponents/OPL/Box/Box';
import { ProfilePageHeader } from '../components/ProfilePageHeader/ProfilePageHeader';
import { ErrorState } from '../../CommonComponents/OPL/AsyncStates/ErrorState/ErrorState';
import { useResetModuleWrapperStyles } from '../../_hooks/useResetModuleWrapperStyles';
import { Wrapper } from '../components/Wrapper/Wrapper';
import { ProfileTablesLoading } from '../components/ProfileTablesLoading/ProfileTablesLoading';
import { WorkflowIdEnum } from '../../Workflow/api/useWorkflowDataQuery';

export interface Props {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
}

export const OtherAssets = ({ clientId, workflowProps }: Props) => {
  useResetModuleWrapperStyles({ isActive: !workflowProps });

  const { isSuccess } = useOtherAssetsQuery({ clientId });

  const [assetToUpsert, setAssetToUpsert] = useState<AssetToUpsert | null>(null);

  return (
    <>
      <ScrollHereOnce options={false} />

      <Wrapper isWorkflow={!!workflowProps}>
        <ProfilePageHeader
          title="Other Assets"
          description="Please include pensions, real estate, business assets, and other miscellaneous assets."
        >
          {isSuccess && (
            <Button leftIcon="fas fa-plus" onClick={() => setAssetToUpsert({})}>
              Add Asset
            </Button>
          )}
        </ProfilePageHeader>

        <OtherAssetsContent
          clientId={clientId}
          workflowProps={workflowProps}
          assetToUpsert={assetToUpsert}
          setAssetToUpsert={setAssetToUpsert}
        />
      </Wrapper>

      {workflowProps && (
        <WorkflowFooter
          isFirstStep={workflowProps.isFirstStep}
          onPrevious={workflowProps.onPrevious}
          onNext={workflowProps.onNext}
        />
      )}
    </>
  );
};

interface OtherAssetsContentProps {
  clientId: string;
  workflowProps?: WorkflowComponentProps;
  assetToUpsert: AssetToUpsert | null;
  setAssetToUpsert: (assetToUpsert: AssetToUpsert | null) => void;
}

const OtherAssetsContent = ({
  clientId,
  workflowProps,
  assetToUpsert,
  setAssetToUpsert,
}: OtherAssetsContentProps) => {
  const [assetToDelete, setAssetToDelete] = useState<AssetToDelete | null>(null);

  const { isLoading, isError, refetch, data } = useOtherAssetsQuery({ clientId });

  const isEstatePlanningWorkflow = workflowProps?.workflowId === WorkflowIdEnum.EstatePlanning;

  if (isLoading) {
    return <ProfileTablesLoading isWorkflow={!!workflowProps} />;
  }

  if (isError) {
    return <ErrorState onAction={refetch} />;
  }

  if (!data) {
    return null;
  }

  return (
    <>
      <Box display="flex" flexDirection="column" gridGap="32" data-testid="other-assets--content">
        <RealEstate
          data={data.Assets.RealEstates}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
        />

        <Business
          data={data.Assets.BusinessAssets}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
        />

        <Pensions
          data={data.Assets.Pensions}
          options={data.Options.PensionOptions}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
        />

        <MiscAssets
          data={data.Assets.MiscellaneousAssets}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
        />

        <DeferredCompensation
          data={data.Assets.DeferredCompensation}
          setAssetToUpsert={setAssetToUpsert}
          setAssetToDelete={setAssetToDelete}
          workflowProps={workflowProps}
        />
      </Box>

      {assetToUpsert && (
        <UpsertAssetModal
          asset={assetToUpsert}
          onClose={() => setAssetToUpsert(null)}
          onAssetTypeChange={assetType => setAssetToUpsert({ OtherAssetType: assetType })}
          assetToDelete={assetToDelete}
          setAssetToDelete={setAssetToDelete}
        >
          {assetToUpsert.OtherAssetType === OtherAssetType.RealEstate && (
            <RealEstateForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as RealEstateDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Business && (
            <BusinessAssetsForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as BusinessAssetDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Pension && (
            <PensionForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as PensionDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.Miscellaneous && (
            <MiscAssetsForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as MiscAssetDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
            />
          )}

          {assetToUpsert.OtherAssetType === OtherAssetType.DeferredCompensation && (
            <DeferredCompensationForm
              clientId={clientId}
              asset={assetToUpsert.Id ? (assetToUpsert as DeferredCompensationDTO) : null}
              options={data.Options}
              onSuccess={() => setAssetToUpsert(null)}
              isEstatePlanningWorkflow={isEstatePlanningWorkflow}
            />
          )}
        </UpsertAssetModal>
      )}

      {assetToDelete && (
        <DeleteAssetModal
          clientId={clientId}
          asset={assetToDelete}
          onClose={() => {
            setAssetToDelete(null);
          }}
          onDelete={() => {
            setAssetToDelete(null);
            setAssetToUpsert(null);
          }}
        />
      )}
    </>
  );
};
